import { TableFooter, TableHead } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { RemittanceRunnerRemittances_remittances } from "@saleor/remittanceRunner/types/RemittanceRunnerRemittances";
import { remittanceRunnerPath } from "@saleor/remittanceRunner/url";
import { openImageInNewTab } from "@saleor/remittanceRunner/utils/handlers";
import { ListProps } from "@saleor/types";
import * as React from "react";
import { FormattedMessage } from "react-intl";

import { useTableStyles } from "../../styles/tableStyles";
import {
  RemittanceRunnerTblColAging,
  RemittanceRunnerTblColDataDate,
  RemittanceRunnerTblColDataText,
  RemittanceRunnerTblColDispatcher,
  RemittanceRunnerTblColPaymentTypeGrossAmount,
  RemittanceRunnerTblColReceiptUploaded,
  RemittanceRunnerTblColStatus
} from "../RemittanceRunnerTableColumns";

interface RemittanceRunnerRemittancesProp extends ListProps {
  remittances: RemittanceRunnerRemittances_remittances;
  disabled: boolean;
}
const numberOfColumns = 7;

export const RemittanceRunnerRemittances: React.FC<RemittanceRunnerRemittancesProp> = props => {
  const {
    remittances,
    disabled,
    settings,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings
  } = props;

  const tblClasses = useTableStyles(props);

  const remittancesList = maybe(
    () => remittances.edges.map(edge => edge.node),
    []
  );

  function getPaymentMethodType(remittance) {
    const paymentMethodType = remittance?.remittanceLines[0]?.order?.payments[0]?.paymentMethodType?.toUpperCase();
    return paymentMethodType;
  }

  return (
    <div className={tblClasses.responsiveTable}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage
                defaultMessage="Remittance ID"
                description="remittance id"
              />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage defaultMessage="Status" description="status" />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage
                defaultMessage="Remittance Date"
                description="remittance date"
              />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage
                defaultMessage="Dispatcher"
                description="dispatcher"
              />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage
                defaultMessage="Payment Type"
                description="payment type"
              />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage defaultMessage="Age" description="age" />
            </TableCellHeader>
            <TableCellHeader
              className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
            >
              <FormattedMessage
                defaultMessage="Receipt Uploaded"
                description="receipt uploaded"
              />
            </TableCellHeader>
          </TableRow>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={numberOfColumns}
              settings={settings}
              hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : false
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {renderCollection(
            remittancesList,
            remittance => (
              <TableRow
                key={remittance.id}
                className={tblClasses.cursorHand}
                onClick={() => {
                  window.open(remittanceRunnerPath(remittance.id));
                }}
              >
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColDataText
                    dataTitle={"Remittance ID"}
                    dataValue={remittance.remittanceNo}
                  />
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColStatus status={remittance.status} />
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColDataDate
                    dataTitle={"Remittance Date"}
                    dataValue={remittance.remittedAt}
                    dateFormat={"MMM DD, YYYY hh:mm A"}
                    centeredText={true}
                  />
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColDispatcher
                    remitter={remittance?.remitter}
                  />
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColPaymentTypeGrossAmount
                    paymentMethodType={getPaymentMethodType(remittance)}
                    amount={remittance?.totalAmount}
                    isRemittanceAmount={true}
                  />
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColAging remittance={remittance} />
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                  onClick={event => {
                    if (remittance?.receiptImage?.url) {
                      event.stopPropagation();
                      openImageInNewTab(remittance?.receiptImage?.url);
                    }
                  }}
                >
                  <RemittanceRunnerTblColReceiptUploaded
                    hasRemittanceObject={true}
                    receiptImage={remittance?.receiptImage}
                  />
                </TableCell>
              </TableRow>
            ),
            () => (
              <TableRow>
                <TableCell colSpan={numberOfColumns}>
                  {"No remittances found"}
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default RemittanceRunnerRemittances;
